import React from "react";
import { useTranslation } from "react-i18next";
import NumberedCard from "./NumberedCard";
import BankBlueIcon from "@/assets/Icons/bank-blue.svg?react";
import IDIcon from "@/assets/Icons/ID.svg?react";
import ArrowBlue from "@/assets/Icons/arrow-blue.svg?react";
import PhoneMockup from "@/assets/Img/webapp-phone-byside-mockup.png";

// Import flag images (following Home.page.tsx convention)
import FlagMexico from "@/assets/Img/Flags/flag-mexico.png";
import FlagGuatemala from "@/assets/Img/Flags/flag-guatemala.png";
import FlagSalvador from "@/assets/Img/Flags/flag-salvador.png";
import FlagNicaragua from "@/assets/Img/Flags/flag-nicaragua.png";
import FlagHonduras from "@/assets/Img/Flags/flag-honduras.png";
import FlagPanama from "@/assets/Img/Flags/flag-panama.png";
import FlagColombia from "@/assets/Img/Flags/flag-colombia.png";
import FlagArgentina from "@/assets/Img/Flags/flag-argentina.png";
import FlagCanada from "@/assets/Img/Flags/flag-canada.png";
import FlagIndia from "@/assets/Img/Flags/flag-india.png";
import FlagPhilipines from "@/assets/Img/Flags/flag-philipines.png";
import FlagVietnam from "@/assets/Img/Flags/flag-vietnam.png";
import FlagPortugal from "@/assets/Img/Flags/flag-portugal.png";
import FlagPoland from "@/assets/Img/Flags/flag-poland.png";
import FlagUS from "@/assets/Img/Flags/flag-us.png";

interface CaboSectionProps {
  country: "USA" | "CAN";
  onClickSignUp: () => void;
}

const CaboSection: React.FC<CaboSectionProps> = ({
  country,
  onClickSignUp,
}) => {
  const { t } = useTranslation("cabo");

  const countryGroupsUSA = [
    {
      title: t("solutions.sendola.america"),
      flags: [
        { src: FlagMexico, alt: "Mexico" },
        { src: FlagArgentina, alt: "Argentina" },
        { src: FlagCanada, alt: "Canada" },
        { src: FlagNicaragua, alt: "Nicaragua" },
        { src: FlagGuatemala, alt: "Guatemala" },
        { src: FlagSalvador, alt: "El Salvador" },
        { src: FlagHonduras, alt: "Honduras" },
        { src: FlagPanama, alt: "Panama" },
        { src: FlagColombia, alt: "Colombia" },
      ],
    },
    {
      title: t("solutions.sendola.asia"),
      flags: [
        { src: FlagIndia, alt: "India" },
        { src: FlagPhilipines, alt: "Philippines" },
        { src: FlagVietnam, alt: "Vietnam" },
      ],
    },
    {
      title: t("solutions.sendola.europe"),
      flags: [
        { src: FlagPortugal, alt: "Portugal" },
        { src: FlagPoland, alt: "Poland" },
      ],
    },
  ];

  return (
    <div className="w-full flex flex-col items-center justify-center py-4 px-4 bg-gradient-to-br from-[#D9E6F8] to-[#fafdff] rounded-3xl shadow-xl relative">
      <div className="flex flex-col md:flex-row w-full max-w-[1100px] gap-6 items-center md:items-start justify-between">
        {/* Phone Image */}
        <div className="flex-shrink-0 flex justify-center items-center lg:-ml-16">
          <img
            src={PhoneMockup}
            alt="Webapp phone mockup"
            className="hidden md:block md:sticky top-0 w-[210px] md:w-[260px] h-auto lg:w-auto lg:h-[400px] drop-shadow-xl"
          />
        </div>
        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center gap-6 w-full">
          <h2 className="text-2xl font-normal text-center max-w-xl">
            {t("solutions.sendola.accessApp")}
          </h2>
          {/* Steps */}
          <div className="flex flex-col lg:flex-row gap-4 w-full justify-center items-center">
            <NumberedCard number={1} className="w-full max-w-[240px]">
              {t("solutions.sendola.signUp")}
            </NumberedCard>
            <NumberedCard number={2} className="w-full max-w-[240px]">
              {t("solutions.sendola.addBeneficiaries")}
            </NumberedCard>
            <NumberedCard number={3} className="w-full max-w-[240px]">
              {t("solutions.sendola.quoteAndSend")}
            </NumberedCard>
          </div>
          {/* Requirements & Countries */}
          <div className="flex flex-col lg:flex-row gap-5 w-full mt-2">
            <div className="w-full lg:w-5/12 bg-gradient-to-br from-[#D9E6F8] to-[#fafdff] rounded-3xl shadow-md p-8 flex flex-col justify-center items-center gap-2">
              <div className="text-[#0A5BFF] text-xs text-center font-semibold mb-1">
                {t("solutions.sendola.beSureYouHave")}:
              </div>
              <div className="flex flex-col flex-1 justify-center gap-2">
                <div className="flex gap-2 items-center">
                  <span role="img" aria-label="US address">
                    <img
                      src={country === "USA" ? FlagUS : FlagCanada}
                      alt="US"
                      className="w-6 h-6"
                    />
                  </span>
                  <span className="text-xs">
                    {country === "USA"
                      ? t("solutions.sendola.usAddress")
                      : t("solutions.sendola.canAddress")}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <BankBlueIcon className="w-6 h-6" />
                  <span className="text-xs">
                    {country === "USA"
                      ? t("solutions.sendola.usBankAccount")
                      : t("solutions.sendola.canBankAccount")}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span role="img" aria-label="ID">
                    <IDIcon className="w-6 h-6" />
                  </span>
                  <span className="text-xs">
                    {t("solutions.sendola.idCard")}
                  </span>
                </div>
              </div>
            </div>
            {country === "USA" ? (
              <div className="w-full lg:w-7/12 bg-gradient-to-br from-[#D9E6F8] to-[#fafdff] rounded-3xl shadow-md p-4 flex-1 flex flex-col justify-center items-center gap-2">
                <div className="text-[#0A5BFF] text-center text-xs font-semibold mb-1">
                  {t("solutions.sendola.availableCountries")}
                </div>
                <div className="flex">
                  <div className="flex flex-col sm:pr-2 w-1/2 lg:w-8/12 gap-2">
                    <div
                      key={countryGroupsUSA[0].title}
                      className="flex flex-col justify-center gap-2"
                    >
                      <span className="font-medium min-w-[70px] text-xs">
                        {countryGroupsUSA[0].title}:
                      </span>
                      <div className="flex gap-2 flex-wrap">
                        {countryGroupsUSA[0].flags.map((flag) => (
                          <img
                            key={flag.alt}
                            src={flag.src}
                            alt={flag.alt}
                            className="w-7 h-7 rounded"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col w-1/2 lg:w-4/12 gap-2">
                    {countryGroupsUSA.slice(1, 3).map((group) => (
                      <div
                        key={group.title}
                        className="flex flex-col justify-center gap-2"
                      >
                        <span className="font-medium min-w-[70px] text-xs">
                          {group.title}:
                        </span>
                        <div className="flex gap-2 flex-wrap">
                          {group.flags.map((flag) => (
                            <img
                              key={flag.alt}
                              src={flag.src}
                              alt={flag.alt}
                              className="w-7 h-7 rounded"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full lg:w-7/12 flex flex-col items-center justify-center gap-2">
                <div className="w-full bg-gradient-to-br from-[#D9E6F8] to-[#fafdff] rounded-3xl shadow-md p-5 py-8 flex-1 flex justify-center items-center justify-self-start gap-2">
                  <div className="w-6/12 gap-1 px-5">
                    <p className="text-[#0A5BFF] text-xs font-medium">
                      {t("solutions.sendola.globalPaymentsEasy")}
                    </p>
                    <p className="text-[9px]">({t("comingSoon")})</p>
                  </div>
                  <div className="flex flex-1 justify-between items-center gap-2 px-2">
                    <img
                      src={FlagCanada}
                      alt="Canada"
                      className="w-8 h-8 rounded"
                    />
                    <ArrowBlue className="w-6 h-6" />
                    <img
                      src={FlagMexico}
                      alt="Mexico"
                      className="w-8 h-8 rounded"
                    />
                  </div>
                </div>
                <div className="flex w-full justify-center mt-4">
                  <button
                    type="button"
                    onClick={onClickSignUp}
                    className="bg-[#0A5BFF] hover:bg-[#0047b3] text-white font-bold py-3 px-8 rounded-full text-lg shadow transition"
                  >
                    {t("solutions.sendola.signUpNow")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {country === "USA" && (
        <div className="flex w-full justify-center mt-8 lg:-mt-4 mb-4">
          <button
            type="button"
            onClick={onClickSignUp}
            className="bg-[#0A5BFF] hover:bg-[#0047b3] text-white font-bold py-3 px-8 rounded-full text-lg shadow transition"
          >
            {t("solutions.sendola.signUpNow")}
          </button>
        </div>
      )}
    </div>
  );
};

export default CaboSection;
